import React from "react";
import * as Mui from "@mui/material";
import { designColors, designWeights } from "services/constants/designTokens";

// Soft sky-blue used only for the illustration's bokeh backdrop — distinct
// from the app's teal accent so the icon reads as neutral/decorative rather
// than as a status color.
const bokehFill = "#EAF3FC";

// Friendly "nothing here" illustration: a sad, torn-edge document with a
// magnifying glass, sitting above a broken dashed shelf line, on a scatter
// of soft circles. Self-contained inline SVG (no image asset to ship/host).
export const NoDataIllustration = ({ size = 132 }) => (
  <svg
    width={size}
    height={(size * 200) / 240}
    viewBox="0 0 240 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="40" cy="35" r="22" fill={bokehFill} opacity="0.9" />
    <circle cx="206" cy="26" r="30" fill={bokehFill} opacity="0.65" />
    <circle cx="218" cy="112" r="16" fill={bokehFill} opacity="0.6" />
    <circle cx="18" cy="142" r="14" fill={bokehFill} opacity="0.6" />
    <circle cx="122" cy="14" r="9" fill={bokehFill} opacity="0.55" />
    <circle cx="58" cy="172" r="8" fill={bokehFill} opacity="0.5" />

    {/* motion / "uh-oh" marks */}
    <line x1="60" y1="20" x2="68" y2="38" stroke={designColors.slate400} strokeWidth="3" strokeLinecap="round" />
    <line x1="50" y1="28" x2="58" y2="46" stroke={designColors.slate400} strokeWidth="3" strokeLinecap="round" />

    {/* dashed shelf, broken under the document */}
    <line x1="18" y1="176" x2="76" y2="176" stroke={designColors.slate300} strokeWidth="4" strokeDasharray="1 10" strokeLinecap="round" />
    <line x1="168" y1="176" x2="222" y2="176" stroke={designColors.slate300} strokeWidth="4" strokeDasharray="1 10" strokeLinecap="round" />

    {/* x mark */}
    <line x1="46" y1="90" x2="62" y2="106" stroke={designColors.slate400} strokeWidth="3.5" strokeLinecap="round" />
    <line x1="62" y1="90" x2="46" y2="106" stroke={designColors.slate400} strokeWidth="3.5" strokeLinecap="round" />

    {/* document with folded corner + torn bottom edge */}
    <path
      d="M90 45 H128 L150 67 V128 L143 138 L136 128 L129 138 L122 128 L115 138 L108 128 L101 138 L94 128 L90 128 Z"
      fill="#F1F5F9"
      stroke={designColors.slate300}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M128 45 L150 67 H128 Z"
      fill={designColors.slate200}
      stroke={designColors.slate300}
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* sad face */}
    <circle cx="106" cy="80" r="2.6" fill={designColors.slate500} />
    <circle cx="128" cy="80" r="2.6" fill={designColors.slate500} />
    <path d="M103 98 Q117 111 132 98" stroke={designColors.slate500} strokeWidth="3" fill="none" strokeLinecap="round" />

    {/* magnifying glass */}
    <circle cx="158" cy="132" r="15" fill="#FFFFFF" stroke={designColors.slate400} strokeWidth="4" />
    <line x1="169" y1="143" x2="183" y2="157" stroke={designColors.slate400} strokeWidth="6" strokeLinecap="round" />
  </svg>
);

// Non-table empty state: illustration + heading + helper text, for cards,
// panels, drawers — anywhere the "no data" state isn't a table row. Shares
// the same illustration and copy styling as TableEmptyState below so every
// empty state in the app (table or not) reads as one consistent component.
export const EmptyState = ({
  title = "No Data Found!",
  subtitle = "There's nothing to show here yet.",
  size = 132,
  sx,
}) => (
  <Mui.Stack
    alignItems="center"
    justifyContent="center"
    spacing={0.5}
    sx={{ py: 4, ...sx }}
  >
    <NoDataIllustration size={size} />
    <Mui.Typography
      sx={{
        fontSize: 16,
        fontWeight: designWeights.semibold,
        color: designColors.slate700,
        mt: 1,
      }}
    >
      {title}
    </Mui.Typography>
    <Mui.Typography
      sx={{
        fontSize: 13,
        color: designColors.slate500,
        textAlign: "center",
        maxWidth: 320,
      }}
    >
      {subtitle}
    </Mui.Typography>
  </Mui.Stack>
);

// Drop-in replacement for the old plain-Alert InfoMessage — same
// (dataLength, colSpan) contract so it slots into an existing
// <TableBody>{empty ? <... /> : rows}</TableBody> branch unchanged.
// `dataLength` is the *unfiltered* source array: empty source → genuinely
// no data; non-empty source but an empty filtered result → a search/filter
// found nothing, so the heading and helper text switch accordingly.
export const TableEmptyState = ({
  dataLength,
  colSpan = 15,
  title,
  subtitle,
  rowHeight = 240,
}) => {
  const isFilteredEmpty = (dataLength?.length ?? 0) > 0;
  const heading = title || (isFilteredEmpty ? "No Matching Results" : "No Data Found!");
  const helper =
    subtitle ||
    (isFilteredEmpty
      ? "Try adjusting your filters or search to find what you're looking for."
      : "There's nothing to show here yet.");

  return (
    <Mui.TableRow>
      <Mui.TableCell colSpan={colSpan} sx={{ border: "none", py: 0 }}>
        <EmptyState title={heading} subtitle={helper} sx={{ minHeight: rowHeight }} />
      </Mui.TableCell>
    </Mui.TableRow>
  );
};
